import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import aiAutomationApi from '../../utils/aiAutomationApi';
import { getCid } from '../../utils/encryptedStorage';
import { handleMailProviderReconnect, isMailProviderAuthError, getGooglePermissionError } from '../../utils/gmailOAuth';
import { useSettings } from '../../contexts/SettingsContext';

const getCandidateName = (item) => (
  item.candidate_name
  || item.candidate?.name
  || item.candidate?.full_name
  || item.candidate_email
  || item.candidate?.email
  || 'Candidate'
);

const getCandidateEmail = (item) => (
  item.candidate_email
  || item.candidate?.email
  || ''
);

const normalizeDraft = (item, index) => ({
  key: String(item.draft_id || item.original_message_id || item.candidate_email || index),
  draftId: item.draft_id,
  candidateName: getCandidateName(item),
  candidateEmail: getCandidateEmail(item),
  question: item.question || item.reply || item.candidate_reply || '',
  subject: item.subject || 'Re: Your Application',
  body: item.draft || item.body || '',
  originalSubject: item.subject || 'Re: Your Application',
  originalBody: item.draft || item.body || '',
  selected: true,
  sent: false,
  error: '',
});

const extractSendError = (err) => {
  const permissionError = getGooglePermissionError(err);
  if (permissionError) return permissionError;
  return err?.response?.data?.detail?.message
    || err?.response?.data?.detail
    || err?.detail
    || err?.message
    || 'Failed to send email. Please try again.';
};

const stripSignature = (value) => String(value || '').replace(
  /\n\n(?:Best regards|Regards|Sincerely|Thanks|Warm regards|Kind regards|Cheers),?\s*(?:\n[\s\S]{0,800})?$/i,
  ''
).trim();

const hasMeaningfulBody = (value) => {
  const cleaned = stripSignature(value)
    .replace(/<[^>]+>/g, ' ')
    .replace(/^(hi|hello|dear)\s+[^,\n]+,?\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned.length >= 25;
};

const BulkReplyDraftModal = ({
  isOpen,
  drafts = [],
  onClose,
  onSent,
}) => {
  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';
  const [items, setItems] = useState([]);
  const [sendingKeys, setSendingKeys] = useState({});
  const [globalError, setGlobalError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setItems((drafts || []).map(normalizeDraft));
    setSendingKeys({});
    setGlobalError('');
  }, [isOpen, drafts]);

  const pendingItems = useMemo(() => items.filter((item) => !item.sent), [items]);
  const selectedPending = useMemo(
    () => items.filter((item) => item.selected && !item.sent),
    [items]
  );

  if (!isOpen) return null;

  const updateItem = (key, updater) => {
    setItems((prev) => prev.map((item) => (
      item.key === key ? { ...item, ...updater(item), error: '' } : item
    )));
  };

  const setItemError = (key, error) => {
    setItems((prev) => prev.map((item) => (
      item.key === key ? { ...item, error } : item
    )));
  };

  const persistAndSend = async (item) => {
    if (!item.draftId) {
      throw new Error('Draft was not saved. Please sync replies and try again.');
    }
    if (!hasMeaningfulBody(item.body)) {
      throw new Error('Draft body is empty. Please regenerate or edit the reply before sending.');
    }
    await aiAutomationApi.put(`/email/drafts/${item.draftId}`, {
      draft_content: item.body,
    });
    await aiAutomationApi.post('/email/drafts/send', {
      draft_ids: [item.draftId],
    });
  };

  const handleGmailAuthError = async (err) => {
    if (!isMailProviderAuthError(err)) return false;
    setGlobalError('Mail provider is not connected. Opening authorization...');
    const connected = await handleMailProviderReconnect({
      cid: getCid(),
      error: err,
      onConnecting: (providerLabel) => setGlobalError(`Connecting ${providerLabel}. Complete authorization in the popup window.`),
      onConnected: (email, providerLabel) => {
        setGlobalError(`${providerLabel} connected${email ? ` (${email})` : ''}. Click Send again to deliver the draft.`);
      },
      onError: (msg) => setGlobalError(`Mail connection failed: ${msg}`),
    });
    if (!connected) {
      setGlobalError('Mail connection was not completed.');
    }
    return true;
  };

  const sendOne = async (item) => {
    setGlobalError('');
    setSendingKeys((prev) => ({ ...prev, [item.key]: true }));
    try {
      await persistAndSend(item);
      setItems((prev) => prev.filter((draftItem) => draftItem.key !== item.key));
      if (onSent) onSent([item]);
    } catch (err) {
      const handledGmail = await handleGmailAuthError(err);
      if (!handledGmail) {
        setItemError(item.key, extractSendError(err));
      }
    } finally {
      setSendingKeys((prev) => ({ ...prev, [item.key]: false }));
    }
  };

  const sendMany = async (targets) => {
    for (const item of targets) {
      // Send sequentially so Gmail/auth failures are surfaced per candidate.
      // eslint-disable-next-line no-await-in-loop
      await sendOne(item);
    }
  };

  const allSelected = pendingItems.length > 0 && pendingItems.every((item) => item.selected);

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm dark:bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-5xl mx-4 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div
          className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"
          style={{ background: `linear-gradient(135deg, ${primaryColor}10, ${secondaryColor}10)` }}
        >
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Reply Drafts</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Review candidate questions and send the prepared replies.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {globalError && (
          <div className="mx-6 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            {globalError}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {items.length === 0 ? (
            <div className="py-14 text-center text-sm text-gray-500 dark:text-gray-400">
              No reply drafts found.
            </div>
          ) : items.map((item) => {
            const isSending = !!sendingKeys[item.key];
            const isEdited = item.subject !== item.originalSubject || item.body !== item.originalBody;
            const canSend = hasMeaningfulBody(item.body);
            return (
              <div
                key={item.key}
                className={`rounded-xl border p-4 ${item.sent ? 'border-green-200 bg-green-50/60 dark:border-green-800 dark:bg-green-900/20' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                    checked={item.selected}
                    disabled={item.sent || isSending}
                    onChange={(event) => updateItem(item.key, () => ({ selected: event.target.checked }))}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{item.candidateName}</h4>
                      {item.candidateEmail && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">{item.candidateEmail}</span>
                      )}
                      {item.sent && (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                          Sent
                        </span>
                      )}
                    </div>

                    {item.question && (
                      <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-900/50 dark:text-gray-300">
                        <div className="mb-1 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Candidate Question</div>
                        <div className="max-h-28 overflow-y-auto whitespace-pre-wrap">{item.question}</div>
                      </div>
                    )}

                    <label className="mt-4 block text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Subject</label>
                    <input
                      value={item.subject}
                      readOnly
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-900/70 dark:text-gray-200"
                    />

                    <label className="mt-4 block text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Reply</label>
                    <textarea
                      value={item.body}
                      disabled={item.sent || isSending}
                      onChange={(event) => updateItem(item.key, () => ({ body: event.target.value }))}
                      className="mt-1 min-h-[180px] w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm leading-6 text-gray-900 focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    />

                    {item.error && (
                      <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
                        {item.error}
                      </div>
                    )}
                    {!item.sent && !canSend && (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                        Draft body is empty. Edit the reply before sending.
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                      {isEdited && !item.sent && (
                        <button
                          type="button"
                          onClick={() => updateItem(item.key, () => ({
                            subject: item.originalSubject,
                            body: item.originalBody,
                          }))}
                          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                          Undo
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={item.sent || isSending || !canSend}
                        onClick={() => sendOne(item)}
                        className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {isSending ? 'Sending...' : item.sent ? 'Sent' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={allSelected}
                disabled={pendingItems.length === 0}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setItems((prev) => prev.map((item) => (
                    item.sent ? item : { ...item, selected: checked }
                  )));
                }}
              />
              Select all pending drafts
            </label>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Close
              </button>
              <button
                type="button"
                disabled={selectedPending.length === 0 || selectedPending.some((item) => !hasMeaningfulBody(item.body))}
                onClick={() => sendMany(selectedPending)}
                className="rounded-lg border px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                style={{ borderColor: primaryColor, color: primaryColor }}
              >
                Send Selected ({selectedPending.length})
              </button>
              <button
                type="button"
                disabled={pendingItems.length === 0 || pendingItems.some((item) => !hasMeaningfulBody(item.body))}
                onClick={() => sendMany(pendingItems)}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                style={{ backgroundColor: primaryColor }}
              >
                Send All ({pendingItems.length})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BulkReplyDraftModal;
